'use client';

import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSidebar } from '@/components/ui/sidebar';
import {
  ChatsSidebarPanel,
  ChatsSidebarRail,
  type ChatsSidebarGroup,
  type ChatsSidebarStatus,
} from '@/features/ai-chat/components/chats-sidebar';
import {
  filterConversations,
  groupConversations,
} from '@/features/ai-chat/lib/conversation-buckets';
import {
  ConversationsAuthError,
  deleteConversation,
  renameConversation,
} from '@/features/ai-chat/lib/conversations';
import {
  conversationsQueryKey,
  listConversationsFor,
  retryConversationList,
  revalidateIdentity,
} from '@/features/ai-chat/lib/chat-identity';
import {
  useChatIdentity,
  useConfirmedChatIdentity,
  useIdentityCheckFailed,
} from '@/features/ai-chat/hooks/use-chat-identity';

/**
 * The app sidebar's content on /ai (AppSidebar morphs into it, same frame as
 * the studio contextual sidebars). Rows are links — `/ai?c=<id>` resumes,
 * `/ai?new=<nonce>` resets — so the chat page only reacts to its URL.
 * `data-ai-chats` scopes the wider widths in globals.css.
 */
export function AiChatsSidebar() {
  return (
    <div data-ai-chats className="flex h-full min-h-0 flex-col">
      {/* Search params need a Suspense boundary in shared chrome; the fallback
          is the same sidebar without an active row. */}
      <React.Suspense fallback={<ChatsSidebar />}>
        <ChatsSidebarWithActive />
      </React.Suspense>
    </div>
  );
}

function ChatsSidebarWithActive() {
  const activeId = useSearchParams().get('c');
  return <ChatsSidebar {...(activeId ? { activeId } : {})} />;
}

function ChatsSidebar({ activeId }: { activeId?: string }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { state, setOpen, isMobile, setOpenMobile } = useSidebar();
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  // One list per signed-in user, fetched only once the server confirmed who
  // that is, so another account's chats never show here (FE defence in depth;
  // taboo-rag binds each thread to its owner).
  const identity = useChatIdentity();
  const confirmed = useConfirmedChatIdentity() === identity;
  const checkFailed = useIdentityCheckFailed();
  const listKey = conversationsQueryKey(identity);
  const {
    data: cached,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) => listConversationsFor(identity, signal),
    enabled: confirmed,
    retry: retryConversationList,
  });
  const data = confirmed ? cached : undefined;
  const failed =
    !(error instanceof ConversationsAuthError) &&
    !data &&
    (Boolean(error) || (!confirmed && checkFailed));
  const retry = () => void revalidateIdentity().then(() => refetch());
  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKey });
  const { mutate: rename } = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => renameConversation(id, title),
    onSuccess: invalidate,
  });
  const { mutate: remove } = useMutation({ mutationFn: deleteConversation, onSuccess: invalidate });

  const newChat = React.useCallback(() => {
    router.push(`/ai?new=${Date.now()}`);
  }, [router]);

  const focusSearch = React.useCallback(() => {
    if (isMobile) setOpenMobile(true);
    else setOpen(true);
    // The input mounts with the expanded panel — focus after that render.
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [isMobile, setOpen, setOpenMobile]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        focusSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusSearch]);

  if (state === 'collapsed' && !isMobile) {
    return (
      <ChatsSidebarRail onNewChat={newChat} onSearch={focusSearch} onExpand={() => setOpen(true)} />
    );
  }

  const status = statusOf({ failed, loading: isLoading || !data }, error, data?.items.length ?? 0);
  const groups: ChatsSidebarGroup[] = groupConversations(
    filterConversations(data?.items ?? [], query)
  ).map((g) => ({
    label: g.bucket,
    items: g.items.map((c) => ({ id: c.id, title: c.title, href: `/ai?c=${c.id}` })),
  }));

  return (
    <ChatsSidebarPanel
      groups={groups}
      status={status}
      query={query}
      onQueryChange={setQuery}
      onNewChat={newChat}
      onRename={(id, title) => rename({ id, title })}
      onDelete={(id) => {
        remove(id);
        if (id === activeId) newChat();
      }}
      searchRef={searchRef}
      onRetry={retry}
      {...(activeId ? { activeId } : {})}
      {...(isMobile
        ? { onNavigate: () => setOpenMobile(false) }
        : { onCollapse: () => setOpen(false) })}
    />
  );
}

function statusOf(
  pending: { failed: boolean; loading: boolean },
  error: unknown,
  count: number
): ChatsSidebarStatus {
  if (error instanceof ConversationsAuthError) return 'guest';
  if (pending.failed) return 'error';
  if (pending.loading) return 'loading';
  if (count === 0) return 'empty';
  return 'ready';
}
